import React, { useState } from 'react';

import useRouter from 'app-migration/hooks/useRouter';
import { observer } from 'mobx-react';
import { createUseStyles } from 'react-jss';

import { citizenshipsMap, orderedCitizenships } from 'constants/citizenships';
import Button from 'DesignSystem2/Button/Button';
import { SelectInput } from 'DesignSystem2/Input';
import Modal from 'DesignSystem2/Modal/Modal';
import Text from 'DesignSystem2/Text';
import { Radius } from 'DesignSystem2/Theme';
import Border from 'DesignSystem2/Theme/Border';
import Padding from 'DesignSystem2/Theme/Padding';
import Palette from 'DesignSystem2/Theme/Palette';
import { SelectOptions } from 'models/constants';
import { createZeroHashHandler } from 'utils/crypto';

import { useStores } from '../hooks/use-stores';

const useStyles = createUseStyles({
    box: {
        borderRadius: Radius.x30,
        border: Border(),
        width: '100%',
    },
});

const CryptoCitizenshipDialog = observer(() => {
    const classes = useStyles();
    const router = useRouter();

    const { uiStore, traderStore } = useStores();
    const dialog = uiStore.cryptoCitizenshipDialog;
    const secondaryDialog = uiStore.cryptoCitizenshipDialog;

    const [invalidCitizenship, setInvalidCitizenship] = useState('');
    const [citizenship, setCitizenship] = useState<SelectOptions>({ label: 'United States', value: 'US' });
    const [attemptedSubmit] = useState(false);
    const [loading, setLoading] = useState(false);

    const content = (
        <div style={{ display: 'flex', flexDirection: 'column', placeItems: 'center', textAlign: 'center' }}>
            <Text.Heading.x15 color={Palette.Text.x10}>
                {`To deposit using Crypto, please enter your citizenship`}
            </Text.Heading.x15>
            <Padding spacing={3} />
            <div className={classes.box}>
                <SelectInput
                    id={'profile-citizenship'}
                    labelText={'Citizenship'}
                    errorText={
                        invalidCitizenship || (attemptedSubmit && citizenship.label === '')
                            ? 'Enter citizenship'
                            : undefined
                    }
                    name={'crypto-citizenship'}
                    value={citizenship.value}
                    onChange={(event): void => {
                        setInvalidCitizenship('');
                        setCitizenship({ label: citizenshipsMap[event.target.value] || '', value: event.target.value });
                    }}
                >
                    <option style={{ display: 'none' }}></option>
                    {orderedCitizenships.map((state) => (
                        <option value={state.label} key={state.label}>
                            {state.value}
                        </option>
                    ))}
                </SelectInput>
            </div>
            <Padding spacing={3} />
            <Button
                variant={'brand'}
                size={'medium'}
                rank={'primary'}
                loading={loading}
                onClick={(): void => {
                    if (citizenship.value !== 'US') {
                        uiStore.pushMessage({
                            text: 'Only US Citizens can deposit via Crypto at this time.',
                            variant: 'negative',
                        });
                        return;
                    }
                    setLoading(true);

                    traderStore.crypto.createCryptoAccount(
                        traderStore.user.getUserId,
                        citizenship.value,
                        (token) => {
                            dialog.close();
                            secondaryDialog.close();
                            // use secondary zerohash store when going to production
                            createZeroHashHandler(token, dialog.callbackOperation);
                        },
                        (error) => {
                            setLoading(false);
                            if (
                                error.error.code === 'document_verification_required' ||
                                error.error.code === 'document_verification_failed'
                            ) {
                                router.push('/sign-up/identity');
                                return;
                            }
                        },
                    );
                }}
                text={'Continue'}
                fullWidth
            />
            <Padding spacing={2} />
            <Button
                variant={'brand'}
                size={'medium'}
                rank={'secondary'}
                onClick={(): void => {
                    dialog.close();
                    secondaryDialog.close();
                }}
                text={'Not now'}
                fullWidth
            />
        </div>
    );

    return (
        <Modal open={dialog.isOpen || secondaryDialog.isOpen} variant={'dialog'}>
            {content}
        </Modal>
    );
});

export default CryptoCitizenshipDialog;
